import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Sparkles, Send, Plus, Search, Pin, PinOff, Trash2, Pencil, Copy, Check,
  ThumbsUp, ThumbsDown, RefreshCw, Bot, User as UserIcon, Lock, MessageSquarePlus,
} from 'lucide-react';
import { toast } from 'sonner';
import { aiAssistantAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { EmptyState } from './common/States';
import { confirmToast } from '../lib/confirmToast';
import { cn, getErrorMessage } from '../lib/utils';

/* ----------------------------- mini markdown ----------------------------- */
const inline = (text) => {
  // escape, then apply inline code / bold / italic / links
  let html = String(text)
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  html = html
    .replace(/`([^`]+)`/g, '<code class="rounded bg-muted px-1.5 py-0.5 text-[0.85em] font-mono text-foreground">$1</code>')
    .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
    .replace(/(^|[^*])\*([^*]+)\*/g, '$1<em>$2</em>')
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noreferrer" class="text-primary underline underline-offset-2">$1</a>');
  return { __html: html };
};

function MiniMarkdown({ text }) {
  const blocks = useMemo(() => {
    const parts = [];
    const segments = String(text || '').split(/```/);
    segments.forEach((seg, i) => {
      if (i % 2 === 1) {
        const nl = seg.indexOf('\n');
        const code = nl >= 0 ? seg.slice(nl + 1) : seg;
        parts.push({ type: 'code', content: code.replace(/\n$/, '') });
      } else if (seg.trim()) {
        parts.push({ type: 'text', content: seg });
      }
    });
    return parts;
  }, [text]);

  const renderText = (content, key) => {
    const lines = content.split('\n');
    const out = [];
    let list = null; let listType = null; let table = null;
    const flushList = () => {
      if (list) {
        const Tag = listType;
        out.push(<Tag key={`l${out.length}`} className={cn('my-1 space-y-0.5 pl-5', listType === 'ul' ? 'list-disc' : 'list-decimal')}>
          {list.map((li, i) => <li key={i} dangerouslySetInnerHTML={inline(li)} />)}
        </Tag>);
        list = null; listType = null;
      }
    };
    const flushTable = () => {
      if (table && table.length) {
        const [head, , ...rows] = table;
        out.push(
          <div key={`t${out.length}`} className="my-2 overflow-x-auto">
            <table className="w-full border-collapse text-sm">
              <thead><tr>{head.map((h, i) => <th key={i} className="border border-border bg-muted px-2 py-1 text-left font-semibold" dangerouslySetInnerHTML={inline(h)} />)}</tr></thead>
              <tbody>{rows.map((r, ri) => <tr key={ri}>{r.map((c, ci) => <td key={ci} className="border border-border px-2 py-1" dangerouslySetInnerHTML={inline(c)} />)}</tr>)}</tbody>
            </table>
          </div>
        );
      }
      table = null;
    };
    lines.forEach((raw) => {
      const line = raw.replace(/\s+$/, '');
      if (/^\s*\|.*\|\s*$/.test(line)) {
        flushList();
        const cells = line.trim().replace(/^\||\|$/g, '').split('|').map((c) => c.trim());
        (table = table || []).push(cells);
        return;
      }
      flushTable();
      const h = line.match(/^(#{1,4})\s+(.*)$/);
      const ul = line.match(/^\s*[-*]\s+(.*)$/);
      const ol = line.match(/^\s*\d+\.\s+(.*)$/);
      if (h) { flushList(); const lvl = h[1].length; out.push(React.createElement(`h${Math.min(lvl + 2, 6)}`, { key: out.length, className: 'mt-2 mb-1 font-semibold text-foreground', dangerouslySetInnerHTML: inline(h[2]) })); }
      else if (ul) { if (listType !== 'ul') { flushList(); listType = 'ul'; list = []; } list.push(ul[1]); }
      else if (ol) { if (listType !== 'ol') { flushList(); listType = 'ol'; list = []; } list.push(ol[1]); }
      else if (line.trim() === '') { flushList(); }
      else { flushList(); out.push(<p key={out.length} className="my-1 leading-relaxed" dangerouslySetInnerHTML={inline(line)} />); }
    });
    flushList(); flushTable();
    return <div key={key}>{out}</div>;
  };

  return (
    <div className="text-sm text-foreground">
      {blocks.map((b, i) => b.type === 'code'
        ? <pre key={i} className="my-2 overflow-x-auto rounded-lg border border-border bg-muted/70 p-3 text-[0.8rem] font-mono leading-relaxed"><code>{b.content}</code></pre>
        : renderText(b.content, i))}
    </div>
  );
}

/* --------------------------------- chat ---------------------------------- */
const TypingDots = () => (
  <div className="flex items-center gap-1 py-1">
    {[0, 1, 2].map((i) => (
      <span key={i} className="h-2 w-2 animate-bounce rounded-full bg-primary/60" style={{ animationDelay: `${i * 0.15}s` }} />
    ))}
  </div>
);

export default function AIAssistant() {
  const { user } = useAuth();
  const [config, setConfig] = useState(null);
  const [loadingConfig, setLoadingConfig] = useState(true);
  const [conversations, setConversations] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [search, setSearch] = useState('');
  const [copiedId, setCopiedId] = useState(null);
  const [agentId, setAgentId] = useState(null);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  const agents = config?.agents || [];
  const activeAgent = agents.find((a) => a.id === agentId);
  const brand = {
    name: activeAgent?.name || config?.assistant_name || 'Copilot',
    color: activeAgent?.theme_color || config?.theme_color || '#3b4fb8',
    welcome: activeAgent?.welcome_message || config?.welcome_message || "Hi! Ask me anything about HR.",
    prompts: config?.suggested_prompts || [],
  };

  const loadConversations = async () => {
    try {
      const res = await aiAssistantAPI.listConversations(search ? { q: search } : undefined);
      setConversations(res.data?.data || []);
    } catch { /* silent */ }
  };

  useEffect(() => {
    (async () => {
      try {
        const res = await aiAssistantAPI.getConfig();
        setConfig(res.data);
        const ags = res.data?.agents || [];
        setAgentId(ags.find((a) => a.is_default)?.id ?? ags[0]?.id ?? null);
      } catch (e) {
        setConfig({ can_use: false, licensed: false });
      } finally {
        setLoadingConfig(false);
      }
      loadConversations();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const t = setTimeout(loadConversations, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, sending]);

  const openConversation = async (id) => {
    setActiveId(id);
    try {
      const res = await aiAssistantAPI.getMessages(id);
      setMessages(res.data?.data || []);
    } catch { setMessages([]); }
  };

  const newChat = () => { setActiveId(null); setMessages([]); setInput(''); inputRef.current?.focus(); };

  const send = async (text) => {
    const content = (text ?? input).trim();
    if (!content || sending) return;
    setInput('');
    setSending(true);
    const optimistic = { id: `tmp-${Date.now()}`, role: 'user', content, created_at: new Date().toISOString() };
    setMessages((m) => [...m, optimistic]);
    try {
      const res = await aiAssistantAPI.chat({ message: content, conversation_id: activeId || undefined, agent_id: agentId || undefined });
      const { conversation_id, message } = res.data || {};
      if (!activeId && conversation_id) { setActiveId(conversation_id); loadConversations(); }
      setMessages((m) => [...m, message]);
      if (activeId) loadConversations();
    } catch (e) {
      toast.error(getErrorMessage(e, 'AI Assistant is unavailable'));
      setMessages((m) => m.filter((x) => x.id !== optimistic.id));
    } finally {
      setSending(false);
    }
  };

  const regenerate = () => {
    const lastUser = [...messages].reverse().find((m) => m.role === 'user');
    if (lastUser) send(lastUser.content);
  };

  const rename = (conv) => {
    const title = window.prompt('Rename conversation', conv.title);
    if (title == null) return;
    aiAssistantAPI.updateConversation(conv.id, { title }).then(() => { toast.success('Renamed'); loadConversations(); });
  };
  const togglePin = (conv) => aiAssistantAPI.updateConversation(conv.id, { pinned: !conv.pinned }).then(loadConversations);
  const del = (conv) => confirmToast({
    message: 'Delete conversation?', description: conv.title, destructive: true, confirmLabel: 'Delete',
    onConfirm: async () => {
      await aiAssistantAPI.deleteConversation(conv.id);
      if (activeId === conv.id) newChat();
      loadConversations();
      toast.success('Conversation deleted');
    },
  });

  const setFeedback = async (msg, value) => {
    const next = msg.feedback === value ? 0 : value;
    setMessages((m) => m.map((x) => (x.id === msg.id ? { ...x, feedback: next } : x)));
    try { await aiAssistantAPI.feedback(msg.id, next); } catch { /* silent */ }
  };

  const copy = (msg) => {
    navigator.clipboard?.writeText(msg.content).then(() => {
      setCopiedId(msg.id); setTimeout(() => setCopiedId(null), 1500);
    });
  };

  if (loadingConfig) {
    return <div className="grid h-[70vh] place-items-center"><TypingDots /></div>;
  }

  if (config && config.can_use === false) {
    return (
      <div className="mx-auto max-w-lg">
        <div className="rounded-2xl border border-warning/30 bg-warning/10 p-8 text-center">
          <div className="mx-auto mb-4 grid h-14 w-14 place-items-center rounded-2xl bg-warning/15 text-amber-600"><Lock className="h-7 w-7" /></div>
          <h2 className="text-lg font-semibold text-foreground">AI Assistant not available</h2>
          <p className="mt-2 text-sm text-muted-foreground">
            {config.licensed === false
              ? 'The AI Assistant add-on is not enabled for your subscription. Ask your administrator to enable it.'
              : 'Your role is not permitted to use the AI Assistant, or it is disabled for this workspace.'}
          </p>
        </div>
      </div>
    );
  }

  const initials = brand.name.split(' ').map((w) => w[0]).join('').slice(0, 2).toUpperCase();

  return (
    <div className="flex h-[calc(100vh-9rem)] gap-4">
      {/* Sidebar */}
      <aside className="hidden w-72 shrink-0 flex-col rounded-2xl border border-border bg-card md:flex">
        <div className="border-b border-border p-3">
          <Button className="w-full justify-start" onClick={newChat}>
            <Plus className="mr-2 h-4 w-4" /> New chat
          </Button>
          <div className="relative mt-3">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search conversations" className="pl-9" />
          </div>
        </div>
        <div className="flex-1 space-y-0.5 overflow-y-auto p-2 hide-scrollbar">
          {conversations.length === 0 ? (
            <p className="px-3 py-8 text-center text-xs text-muted-foreground">No conversations yet</p>
          ) : conversations.map((c) => (
            <div key={c.id} className={cn('group flex items-center gap-1 rounded-lg px-2 py-2 transition-colors',
              activeId === c.id ? 'bg-primary/10' : 'hover:bg-muted')}>
              <button className="flex min-w-0 flex-1 items-center gap-2 text-left" onClick={() => openConversation(c.id)}>
                {c.pinned && <Pin className="h-3 w-3 shrink-0 text-primary" />}
                <span className={cn('truncate text-sm', activeId === c.id ? 'font-medium text-foreground' : 'text-muted-foreground')}>{c.title}</span>
              </button>
              <div className="flex shrink-0 items-center opacity-0 transition-opacity group-hover:opacity-100">
                <button className="rounded p-1 text-muted-foreground hover:text-foreground" title={c.pinned ? 'Unpin' : 'Pin'} onClick={() => togglePin(c)}>{c.pinned ? <PinOff className="h-3.5 w-3.5" /> : <Pin className="h-3.5 w-3.5" />}</button>
                <button className="rounded p-1 text-muted-foreground hover:text-foreground" title="Rename" onClick={() => rename(c)}><Pencil className="h-3.5 w-3.5" /></button>
                <button className="rounded p-1 text-muted-foreground hover:text-danger" title="Delete" onClick={() => del(c)}><Trash2 className="h-3.5 w-3.5" /></button>
              </div>
            </div>
          ))}
        </div>
      </aside>

      {/* Chat window */}
      <section className="flex min-w-0 flex-1 flex-col rounded-2xl border border-border bg-card">
        <header className="flex items-center gap-3 border-b border-border px-4 py-3">
          <div className="grid h-9 w-9 place-items-center rounded-xl text-sm font-bold text-white" style={{ background: brand.color }}>{initials || <Bot className="h-5 w-5" />}</div>
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold text-foreground">{brand.name}</p>
            <p className="text-xs text-muted-foreground">{config?.provider ? `Powered by ${config.provider}` : 'AI Assistant'}</p>
          </div>
          {agents.length > 1 ? (
            <Select value={agentId ? String(agentId) : undefined} onValueChange={(v) => { setAgentId(Number(v)); newChat(); }}>
              <SelectTrigger className="ml-auto h-9 w-[190px]"><SelectValue placeholder="Choose agent" /></SelectTrigger>
              <SelectContent>
                {agents.map((a) => <SelectItem key={a.id} value={String(a.id)}>{a.name}</SelectItem>)}
              </SelectContent>
            </Select>
          ) : (
            <Badge variant="soft" className="ml-auto"><Sparkles className="mr-1 h-3 w-3" /> AI</Badge>
          )}
        </header>

        <div ref={scrollRef} className="flex-1 space-y-5 overflow-y-auto p-4 md:p-6">
          {messages.length === 0 && !sending ? (
            <div className="mx-auto max-w-xl py-6 text-center">
              <div className="mx-auto mb-4 grid h-16 w-16 place-items-center rounded-2xl text-white" style={{ background: brand.color }}><Sparkles className="h-8 w-8" /></div>
              <h2 className="text-lg font-semibold text-foreground">{brand.welcome}</h2>
              {brand.prompts.length > 0 && (
                <div className="mt-5 grid gap-2 sm:grid-cols-2">
                  {brand.prompts.slice(0, 4).map((p, i) => (
                    <button key={i} onClick={() => send(p)} className="card-interactive rounded-xl border border-border bg-card px-4 py-3 text-left text-sm text-foreground">
                      <MessageSquarePlus className="mb-1 h-4 w-4 text-primary" />
                      {p}
                    </button>
                  ))}
                </div>
              )}
            </div>
          ) : (
            messages.filter((m) => m.role !== 'system').map((m) => (
              <div key={m.id} className={cn('flex gap-3', m.role === 'user' ? 'flex-row-reverse' : '')}>
                <div className={cn('grid h-8 w-8 shrink-0 place-items-center rounded-lg text-white', m.role === 'user' ? 'bg-slate-500' : '')}
                  style={m.role === 'assistant' ? { background: brand.color } : undefined}>
                  {m.role === 'user' ? <UserIcon className="h-4 w-4" /> : <Bot className="h-4 w-4" />}
                </div>
                <div className={cn('group min-w-0 max-w-[80%] rounded-2xl px-4 py-2.5',
                  m.role === 'user' ? 'bg-primary text-primary-foreground' : 'border border-border bg-muted/40')}>
                  {m.role === 'user'
                    ? <p className="whitespace-pre-wrap text-sm">{m.content}</p>
                    : <MiniMarkdown text={m.content} />}
                  {m.role === 'assistant' && (
                    <div className="mt-2 flex items-center gap-1 border-t border-border/60 pt-2 opacity-0 transition-opacity group-hover:opacity-100">
                      <button className="rounded p-1 text-muted-foreground hover:text-foreground" title="Copy" onClick={() => copy(m)}>{copiedId === m.id ? <Check className="h-3.5 w-3.5 text-success" /> : <Copy className="h-3.5 w-3.5" />}</button>
                      <button className={cn('rounded p-1 hover:text-success', m.feedback === 1 ? 'text-success' : 'text-muted-foreground')} title="Good response" onClick={() => setFeedback(m, 1)}><ThumbsUp className="h-3.5 w-3.5" /></button>
                      <button className={cn('rounded p-1 hover:text-danger', m.feedback === -1 ? 'text-danger' : 'text-muted-foreground')} title="Bad response" onClick={() => setFeedback(m, -1)}><ThumbsDown className="h-3.5 w-3.5" /></button>
                      <button className="rounded p-1 text-muted-foreground hover:text-foreground" title="Regenerate" onClick={regenerate}><RefreshCw className="h-3.5 w-3.5" /></button>
                    </div>
                  )}
                </div>
              </div>
            ))
          )}
          {sending && (
            <div className="flex gap-3">
              <div className="grid h-8 w-8 shrink-0 place-items-center rounded-lg text-white" style={{ background: brand.color }}><Bot className="h-4 w-4" /></div>
              <div className="rounded-2xl border border-border bg-muted/40 px-4 py-2.5"><TypingDots /></div>
            </div>
          )}
        </div>

        <div className="border-t border-border p-3">
          <form className="flex items-end gap-2" onSubmit={(e) => { e.preventDefault(); send(); }}>
            <textarea
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
              placeholder={`Message ${brand.name}…`}
              rows={1}
              className="max-h-40 min-h-[44px] flex-1 resize-none rounded-xl border border-input bg-muted/60 px-3.5 py-2.5 text-sm text-foreground placeholder:text-muted-foreground/70 focus-visible:border-primary focus-visible:bg-card focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-primary/15"
            />
            <Button type="submit" size="icon" className="h-11 w-11 shrink-0" disabled={sending || !input.trim()}>
              <Send className="h-4 w-4" />
            </Button>
          </form>
          <p className="mt-1.5 px-1 text-[11px] text-muted-foreground">AI can make mistakes. Verify important HR information.</p>
        </div>
      </section>
    </div>
  );
}
